import React from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {ArrowRight, CheckCircle, Robot, Users} from '../../icons';
import {clearDecisions} from '../wizardStorage';
import {useOnboarding} from '../wizardContext';
import {useLearning} from '../../learn/useLearning';
import {essentialsOf} from '../../learn/state/learnModel';
import {CLARITY_UNMASK} from '../../../utils/clarity/unmask';

/** El Tablero de la 2.1: la misma ruta que su entrada en el riel (`SidebarV2`). */
export const DASHBOARD_PATH = '/marketing';
export const CLIENTS_PATH = '/agency/clients';
export const LEARN_PATH = '/aprende';

/**
 * El cierre del recorrido: **una pregunta, no una recomendación**.
 *
 * Esta pantalla empujaba un solo camino —conectar la IA— con la salida al tablero en segundo plano,
 * y ofrecía además Memoria y Media Plan. La reunión del 17/09 (card gY5woCDz) la dio vuelta por dos
 * motivos:
 *
 * - **Hay dos perfiles y los dos terminan acá.** El que vive en ChatGPT o Claude, y el usuario
 *   tradicional de Dashbo que quiere sus informes y sus tableros. Pablo: «tener cuidado con eso,
 *   porque no todos los usuarios son fede. Muchos van a venir a Dashbo porque quieren Dashbo». Una
 *   pantalla que destaca un camino le dice al otro perfil que se equivocó de producto.
 * - **Memoria y Media Plan no significan nada todavía.** «Si me pongo en los zapatos de alguien que
 *   no tiene ni idea, ¿qué es la memoria?, ¿qué es el media plan? No sé si es el momento de hablarles
 *   de esto». Se fueron: la Memoria se sigue ofreciendo desde «Tus primeros pasos», que es donde
 *   aparece cuando la agencia ya tiene con qué entenderla.
 *
 * Quedan dos destinos y una salida para el que ya conoce Dashbo.
 *
 * **«Ver mis clientes» es el principal desde el 14/10** (H-05, recorrido con Pablo del 13/10): el
 * momento en que Dashbo «hace clic» es ver los clientes con sus presupuestos, y eso ya está cargado
 * cuando termina el alta. Aprende se OFRECE al pie, con lo que cuesta («6 tareas, unos 15 minutos»),
 * y no se impone: Pablo, al abrirlo, «veo todo este texto, digo, no es para mí, prefiero ir y mirar
 * la pantalla y tocar».
 */
export function StepDone() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {state} = useOnboarding();

    // Aprende sólo se ofrece si la persona tiene la página: sin ella `/aprende` no muestra nada. El
    // número de tareas sale de la pista de lo esencial, no de un 6 escrito a mano.
    const learning = useLearning().model;
    const essentials = learning?.enabled === true ? essentialsOf(learning) : null;

    const clients = state.apply.createdClients.length;
    const skipped = state.apply.skipped || [];
    const partial = partialReadinessOf(state.apply.readiness);

    // El borrador del recorrido se limpia SIEMPRE al salir, vaya a donde vaya: quedó aplicado, y
    // conservarlo haría que volver al túnel reabra una cartera que ya existe.
    const goTo = (path) => {
        clearDecisions();
        navigate(path);
    };
    // «Ir a mi tablero» va al Tablero (H-05). Desde el D8 (card HMy64fKi) caía en el listado de
    // clientes, igual que «Ver mis clientes»: dos salidas con nombres distintos al mismo lugar.
    const goToDashboard = () => goTo(DASHBOARD_PATH);

    return (
        <div className="ob-step-body">
            <header className="ob-head -center">
                <span className="ob-done-check"><CheckCircle size={34} weight="fill"/></span>
                <h1 {...CLARITY_UNMASK}>{t(partial ? 'onboarding.done.partial_title' : 'onboarding.done.title')}</h1>
                {/* D5: con clientes todavía preparándose, la pantalla dice cuántos están listos y
                    que el resto sigue solo. Antes decía «Listo: tu cartera está en Dashbo / 5
                    clientes con su presupuesto…» con 3 de 5 listos (card 9WXeowAo). El total va como
                    `count` para que i18next elija el plural: con un solo cliente no se lee «0 de 1
                    clientes». */}
                {/* Los números vienen de la respuesta del alta y NO se persisten: recargar esta
                    pantalla los pierde. Antes eso mostraba "0 clientes y 0 líneas" justo después de
                    que la agencia armó su cartera; sin números, la frase sigue siendo verdadera. */}
                {/* El número va en su propia clave pluralizada porque i18next elige singular/plural
                    con UN solo `count`. Ya no cuenta líneas de control: en el alta no se usa el
                    término (card mFHzKeNK), así que se dice lo que la agencia ganó —sus clientes con
                    el presupuesto en seguimiento—.
                    Se condiciona por CLIENTES y no por líneas: en un reintento después de un 422, o
                    cuando las cuentas van a clientes que ya existían, la corrida crea líneas sin crear
                    clientes (`created_clients: []`), y la frase diría «0 clientes con su presupuesto
                    en seguimiento». La respuesta no dice de qué cliente es cada línea, así que no se
                    puede contar mejor: sin clientes nuevos va la frase sin números, que sigue siendo
                    verdadera. */}
                {/* Con HTML porque el número va en negrita, como en el mockup: es el logro que la
                    pantalla viene a contar. Lo único interpolado es otra clave nuestra con un entero,
                    así que no entra texto del usuario ni del backend. */}
                {partial ? (
                    <PartialClosing partial={partial}/>
                ) : (
                    <p dangerouslySetInnerHTML={{__html: clients > 0
                        ? t('onboarding.done.subtitle', {
                            clients: t('onboarding.done.client_count', {count: clients}),
                            interpolation: {escapeValue: false},
                        })
                        : t('onboarding.done.subtitle_plain')}}/>
                )}
                {skipped.length > 0 && <SkippedAccounts skipped={skipped}/>}
            </header>

            <p className="ob-next-question" {...CLARITY_UNMASK}>{t('onboarding.done.next_question')}</p>

            <div className="ob-duo -choices">
                <article className="ob-duo__card">
                    <span className="ob-duo__icon"><Robot size={20}/></span>
                    <h3 {...CLARITY_UNMASK}>{t('onboarding.done.ia_title')}</h3>
                    <p {...CLARITY_UNMASK}>{t('onboarding.done.ia_text')}</p>
                    {/* Sin `clearDecisions`: este camino NO sale del túnel. El paso de la IA usa la
                        cartera recién creada para nombrar un cliente real en la pregunta de
                        ejemplo; limpiarla acá lo dejaría preguntando por nadie. */}
                    <button type="button" className="ob-btn -ia" {...CLARITY_UNMASK}
                            onClick={() => navigate('/onboarding/ia')}>
                        {t('onboarding.done.ia_cta')} <ArrowRight size={15}/>
                    </button>
                </article>
                {/* El otro perfil. Cae en sus clientes y no en el tablero a propósito: ahí es donde
                    «Tus primeros pasos» lo sigue guiando, en vez de dejarlo solo frente a una
                    pantalla llena de números que todavía no sabe leer. */}
                <article className="ob-duo__card">
                    <span className="ob-duo__icon"><Users size={20}/></span>
                    <h3 {...CLARITY_UNMASK}>{t('onboarding.done.clients_title')}</h3>
                    <p {...CLARITY_UNMASK}>{t('onboarding.done.clients_text')}</p>
                    <button type="button" className="ob-btn -primary" {...CLARITY_UNMASK}
                            onClick={() => goTo(CLIENTS_PATH)}>
                        {t('onboarding.done.clients_cta')} <ArrowRight size={15}/>
                    </button>
                </article>
            </div>

            {/* Al pie y en voz baja: aprender lo esencial, para quien quiere que lo lleven, y el
                atajo del que ya conoce Dashbo. No es la renuncia que había antes («lo hago
                después», que sonaba a abandonar algo): son respuestas legítimas a la pregunta de
                arriba. */}
            <footer className="ob-foot -center ob-done-foot">
                {essentials && (
                    <button type="button" className="ob-link" onClick={() => goTo(LEARN_PATH)}>
                        {t('onboarding.done.learn_cta', {count: essentials.total})}
                    </button>
                )}
                <button type="button" className="ob-link -muted" onClick={goToDashboard}>
                    {t('onboarding.done.skip_all')}
                </button>
            </footer>
        </div>
    );
}

/**
 * `{ready, total, failed, pending}` si al salir de «Creando tu cartera» quedaban clientes sin
 * terminar, o `null`. `pending` son los que todavía se preparan (en curso, en espera o
 * reintentando): los únicos de los que se puede decir que «aparecen solos».
 *
 * `null` también cuando no se supo (backend sin progreso por cliente, o una recarga que perdió la
 * memoria de la pestaña): ahí queda el cierre de siempre, que no afirma números.
 */
export function partialReadinessOf(readiness) {
    if (!readiness || !(readiness.total > 0)) return null;
    const {ready, total} = readiness;
    if (ready >= total) return null;
    // Un conteo guardado antes de que viajaran los fallidos se lee como «ninguno falló».
    const failed = Math.min(readiness.failed || 0, total - ready);
    return {ready, total, failed, pending: total - ready - failed};
}

/**
 * Qué dice el cierre cuando no todo quedó listo (C-tunel-03).
 *
 * Sin fallidos, una frase: «N de M listos; el resto se sigue preparando y aparece solo». Con
 * fallidos esa frase mentía —un cliente que falló no aparece solo—, así que se dice cada grupo por
 * separado: cuántos están listos (con un solo cliente sobra), cuántos se siguen preparando si queda
 * alguno, y para los fallidos la MISMA frase que ya leyó en «Creando tu cartera», que manda a su ficha.
 */
function PartialClosing({partial}) {
    const {t} = useTranslation();

    if (!partial.failed) {
        return <p>{t('onboarding.done.partial_text', {ready: partial.ready, count: partial.total})}</p>;
    }
    return (
        <>
            {partial.total > 1 && (
                <p>{t('onboarding.done.partial_ready', {ready: partial.ready, total: partial.total})}</p>
            )}
            {partial.pending > 0 && <p>{t('onboarding.done.pending_note', {count: partial.pending})}</p>}
            <p>{t('onboarding.applying.failed_note', {count: partial.failed})}</p>
        </>
    );
}

/**
 * Las cuentas que no necesitaron línea porque YA tenían una.
 *
 * El conteo solo dejaba al usuario con la pregunta abierta —«¿cuáles, y por qué?»— y es justo el
 * caso en que el recorrido puede terminar con «0 líneas creadas», que sin explicación se lee como
 * que el alta falló. El backend manda por cada una la línea que la cubre desde el día uno; acá sólo
 * faltaba mostrarlo.
 */
function SkippedAccounts({skipped}) {
    const {t} = useTranslation();

    return (
        <div className="ob-skipped">
            <p className="ob-note">{t('onboarding.done.skipped', {count: skipped.length})}</p>
            <ul className="ob-skipped__list">
                {skipped.map(account => (
                    <li key={`${account.channel}:${account.account_id}`}>
                        <span className="ob-skipped__account">
                            {account.account_name || account.account_id}
                        </span>
                        <span className="ob-skipped__covered">
                            {t('onboarding.done.skipped_covered_by',
                               {line: account.covering_business_unit_name,
                                interpolation: {escapeValue: false}})}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
